import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';
import type { BudgetGroup, CreateOrganisationNodeBudgetParams, Organisation, OrganisationNodeBudget, OrganisationNodeBudgetKeyValues, OrganisationNodeBudgetSummaryKeyValues } from '@sysarb/sysarb-types/services/v1/pay-management';
import type { Guid } from 'guid-typescript';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import type { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';

const payManagementService = new RestManager(`https://${getApiHost()}/pay-management-service`);

export function abort(): void {
    payManagementService.abort();
}

/**
 * Create organisation node budget
 *  @param reviewId review id
 */
export async function createOrganisationNodeBudget(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<OrganisationNodeBudget | null> {
    const payload: CreateOrganisationNodeBudgetParams = {
        reviewId,
    };
    const promise = resgate.call<OrganisationNodeBudget>('v1.pay-management.organisationnode-budget', 'create', payload).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Update organisation node budget
 *  @param orgNodeBudgetId budget id
 */
export function updateOrganisationNodeBudget(resgate: ReturnType<typeof useResgate>, orgNodeBudgetId: string, budget: OrganisationNodeBudget): void {
    const payload: OrganisationNodeBudget = {
        id: budget.id,
        createdAt: budget.createdAt,
        updatedAt: budget.updatedAt,
        name: budget.name,
        description: budget.description,
        generalDone: budget.generalDone,
        selectionDone: budget.selectionDone,
        calculateDone: budget.calculateDone,
        reviewId: budget.reviewId,
        budgetValue: budget.budgetValue,
        excludedOrganisationNodes: budget.excludedOrganisationNodes
    };
    void resgate.call(`v1.pay-management.organisationnode-budget.${orgNodeBudgetId}`, 'update', payload).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
}

/**
 * Delete budget
 *  @param budgetId budget id
 */
export async function deleteOrganisationNodeBudget(resgate: ReturnType<typeof useResgate>, budgetId: string): Promise<OrganisationNodeBudget | null> {
    const promise = resgate.call<OrganisationNodeBudget>(`v1.pay-management.organisationnode-budget.${budgetId}`, 'delete', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get organisation node budget key values
 *  @param orgNodeBudgetId review id
 */
export async function getOrganisationNodeBudgetKeyvalues(resgate: ReturnType<typeof useResgate>, orgNodeBudgetId: string): Promise<OrganisationNodeBudgetKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<OrganisationNodeBudgetKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.organisationnode-budget.${orgNodeBudgetId}.keyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch all employments from a reviewDraft id.
 * @param salaryReviewId Id of the pay management review
 * @returns employments filtered on the pay management review
 */
export async function getOrganisationNodes(salaryReviewId: string): Promise<Organisation | null> {
    const { instance } = useLoginStore();
    return payManagementService.post<Organisation | null>({}, `v1/${instance}/pay-management/organisation-nodes/${salaryReviewId}`);
}

/**
 * Get all orgnisation node budgets
 *  @param reviewId review id
 *  @param salaryRaiseColumn salaryRaiseColumn on the budget
 */
export async function getOrganisationNodeBudgetsCollection(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<Iterable<OrganisationNodeBudget> | null> {
    const promise = resgate.collection<OrganisationNodeBudget>(`v1.pay-management.organisationnode-budget.${reviewId}.getbyreviewid`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get summary key values for organisation node budgets
 *  @param reviewId review id
 */
export async function getOrganisationNodeSummaryKeyValues(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<OrganisationNodeBudgetSummaryKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<OrganisationNodeBudgetSummaryKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.organisationnode-budget.${reviewId}.summarykeyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get menu list
 *  @param salaryReviewId salary review id
 *  @param currentRole current user role
 *  @param currentOrganisationId current organisation id
 *  @param componentId component id
 */
export async function organisationBudgetValues(resgate: ReturnType<typeof useResgate>, salaryReviewId: Guid, currentRole: Role, currentOrganisationId: string): Promise<Iterable<BudgetGroup>| null> {
    let endpoint = '';

    switch (currentRole) {
        case Role.Manager:
            endpoint = 'managerorganisationbudgetvalues';
            break;
        case Role.SectionManager:
            endpoint = 'sectionmanagerorganisationbudgetvalues';
            break;
        default:
            break;
    }

    if (endpoint === '') {
        return Promise.resolve(null);
    }
    const { instanceTopic } = useLoginStore();

    const promise = resgate.collection<BudgetGroup>(`v1.pay-management.budget.${instanceTopic}.${currentOrganisationId}.${salaryReviewId}.${endpoint}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });

    return promise;
}
